import { firstValueFrom } from 'rxjs'
import type { IOrder } from '../../../Model/Order/IOrder.ts'
import type { ServerSnapshot } from '../../../Services/OrderWizard/reconcile.ts'
import type { WizardDependencies } from './draftState.ts'

export const isNotFound = (error: unknown) => {
  const status = (error as { status?: number } | null)?.status
  return status === 404 || status === 410
}

export type OrderState = {
  snapshot: ServerSnapshot
  /** The order as read, unless it is gone. */
  order?: IOrder
  /** The platform each item's forward file was detected as, by server file id. */
  platforms: Record<string, string>
}

/** What the server holds of an order: the order, its pools' items and their uploaded files. */
export const fetchOrderState = async (
  orderId: string,
  repositories: Pick<WizardDependencies, 'OrderRepository' | 'PoolUploadRepository'>
): Promise<OrderState> => {
  let found: IOrder
  try {
    found = await firstValueFrom(repositories.OrderRepository.getOrder(orderId))
  } catch (error) {
    if (isNotFound(error)) {
      return { snapshot: { order: 'missing', pools: [], completed: {} }, platforms: {} }
    }
    throw error
  }
  if (found.deletedAt) {
    return { snapshot: { order: 'missing', pools: [], completed: {} }, platforms: {} }
  }
  if (found.orderSubmission) {
    return { snapshot: { order: { submitted: true }, pools: [], completed: {} }, order: found, platforms: {} }
  }
  const pools = await firstValueFrom(repositories.OrderRepository.getAllSamplePools({ orderId }))
  const completed = Object.fromEntries(
    await Promise.all(
      pools.map(
        async (pool) =>
          [pool.id, (await repositories.PoolUploadRepository.completedPoolFiles(orderId, pool.id)).map(({ id, fileName }) => ({ id, fileName }))] as const
      )
    )
  )
  const items = pools.map((pool) => ({ pool, items: [...(pool.samples ?? []), ...(pool.negativeControls ?? [])] }))
  return {
    order: found,
    snapshot: {
      order: { submitted: false },
      pools: items.map(({ pool, items: poolItems }) => ({
        id: pool.id,
        items: poolItems.map(({ id, forwardFileId, reverseFileId, itemType, forwardPrimer, reversePrimer }) => ({
          id,
          forwardFileId,
          reverseFileId,
          hasPrimers: !!forwardPrimer && !!reversePrimer,
          itemType
        }))
      })),
      completed,
      settings: {
        targetTaxonomy: found.targetTaxonomy as NonNullable<ServerSnapshot['settings']>['targetTaxonomy'],
        processingType: found.processingType,
        allowSingletons: !!found.allowSingletons,
        targetRegion: found.targetRegion,
        sampleConfiguration: found.sampleConfiguration
      }
    },
    platforms: Object.fromEntries(
      items.flatMap(({ items: poolItems }) =>
        poolItems.flatMap((item) => (item.forwardFileId && item.detectedSequencingPlatform ? [[item.forwardFileId, item.detectedSequencingPlatform]] : []))
      )
    )
  }
}
